<template>
    <Card>
        <CardHeader>
            <div class="flex items-center gap-2">
                <KeyRound class="size-4 text-muted-foreground" />
                <CardTitle class="text-sm font-medium text-primary">
                    API Keys
                </CardTitle>
            </div>
        </CardHeader>

        <CardContent class="space-y-6">
            <!-- Public Key Section -->
            <div>
                <div class="mb-1.5 flex items-center justify-between">
                    <Label for="public-key" class="text-sm font-medium text-primary">
                        Public API key
                    </Label>
                    <span class="inline-flex items-center justify-center border border-new-green-600 bg-new-green-100 px-2 py-0.5 text-xs font-semibold leading-4 text-new-green-600">
                        Safe to share
                    </span>
                </div>
                <div class="flex flex-col gap-2 sm:flex-row">
                    <Input
                        id="public-key"
                        :model-value="apiToken.token"
                        readonly
                        class="font-mono text-base md:text-sm"
                    />
                    <Button
                        variant="outline"
                        size="icon"
                        class="self-start"
                        @click="copyPublicKey"
                    >
                        <Copy class="size-4 text-new-neutral-400" />
                    </Button>
                </div>
            </div>

            <!-- Secret Key Section -->
            <div>
                <div class="mb-1.5 flex items-center justify-between">
                    <Label for="secret-key" class="text-sm font-medium text-new-neutral-700">
                        Secret key
                    </Label>
                    <span class="inline-flex items-center justify-center border border-new-red-600 bg-new-red-100 px-2 py-0.5 text-xs font-semibold leading-4 text-new-red-500">
                        Never expose
                    </span>
                </div>
                <div class="flex flex-col gap-2 sm:flex-row">
                    <Input
                        id="secret-key"
                        :model-value="
                            secretKeyRevealed
                                ? secretKey
                                : secretKey.replace(/./g, '•')
                        "
                        readonly
                        class="font-mono text-base md:text-sm"
                    />
                    <div class="flex gap-2">
                        <Button
                            variant="outline"
                            size="icon"
                            :disabled="!secretKeyRevealed"
                            @click="copySecretKey"
                        >
                            <Copy class="size-4 text-new-neutral-400" />
                        </Button>
                        <Button
                            variant="outline"
                            size="icon"
                            :disabled="secretKeyRevealed || secretKeySeen"
                            @click="revealSecretKey"
                        >
                            <Eye
                                v-if="!secretKeyRevealed"
                                class="size-4 text-new-neutral-400"
                            />
                            <EyeOff
                                v-else
                                class="size-4 text-new-neutral-400"
                            />
                        </Button>
                        <Button
                            variant="outline"
                            @click="regenerateSecretKey"
                        >
                            <RefreshCw class="mr-2 size-4 text-new-neutral-400" />
                            Regenerate
                        </Button>
                    </div>
                </div>

                <!-- Warning Banner -->
                <div class="mt-3 flex items-center gap-3 bg-new-amber-100 pt-1 pr-6 pb-1.5 pl-3">
                    <TriangleAlert class="size-3.5 shrink-0 text-new-amber-600" />
                    <p class="text-[10px] leading-[14px] text-new-amber-600">
                        {{
                            secretKeySeen
                                ? 'Secret key already viewed — regenerate to see a new one.'
                                : 'Keep your secret key secure. It can only be viewed once.'
                        }}
                    </p>
                </div>
            </div>

            <!-- Quick Copy Section -->
            <div>
                <div class="mb-2 flex items-center gap-2">
                    <h3 class="text-sm font-medium text-primary">Quick copy</h3>
                </div>
                <div class="overflow-hidden rounded-md bg-new-neutral-100">
                    <div class="flex items-center justify-between border-b border-new-neutral-200 px-2 py-1">
                        <div class="flex items-center gap-2">
                            <FileCode class="size-4 text-new-neutral-600" />
                            <span class="font-mono text-sm text-new-neutral-500">.env.local</span>
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            @click="copyEnvCode"
                        >
                            <Copy class="size-4" />
                        </Button>
                    </div>
                    <div class="overflow-x-auto p-3 font-mono text-xs">
                        <div class="flex gap-3">
                            <div class="select-none text-new-neutral-400">
                                <div>1</div>
                                <div>2</div>
                            </div>
                            <div class="min-w-0">
                                <div class="text-primary whitespace-nowrap">
                                    <span>TOPOL_PUBLIC_KEY</span>=<span>{{ apiToken.token }}</span>
                                </div>
                                <div class="text-primary whitespace-nowrap">
                                    <span>TOPOL_SECRET_KEY</span>=<span>{{ secretKeyRevealed ? secretKey : '•••••••••••••' }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </CardContent>
    </Card>
</template>

<script lang="ts" setup>
import { PropType, ref, onMounted, computed } from 'vue';
import { router } from '@inertiajs/vue3';
import axios from 'axios';
import { route } from 'ziggy-js';
import { useClipboard } from '@vueuse/core';
import { toast } from 'vue-sonner';
import IToken from '@/types/Plugin/IToken';
import { Button } from '@/components/ui/button';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
    KeyRound,
    Copy,
    Eye,
    EyeOff,
    RefreshCw,
    TriangleAlert,
    FileCode,
} from 'lucide-vue-next';

const props = defineProps({
    apiToken: {
        type: Object as PropType<IToken>,
        required: true,
    },
});

const clipboard = useClipboard();
const defaultSecretKey = 'e1sbRX0Mpx6FyhmM4KPx8c3xw2uCOSXnSUx7XRJ64s9gNB8XdeMKLJQ4xt';
const secretKey = ref(defaultSecretKey);
const secretKeyRevealed = ref(false);
const secretKeySeen = ref(false);

const envCode = computed(() => {
    return `TOPOL_PUBLIC_KEY=${props.apiToken.token}\nTOPOL_SECRET_KEY=${secretKeyRevealed.value ? secretKey.value : '•••••••••••••'}`;
});

onMounted(() => {
    fetchSecretKeySeen();
});

const copyPublicKey = () => {
    clipboard.copy(props.apiToken.token);
    toast.success('Copied to clipboard!');
};

const copySecretKey = () => {
    clipboard.copy(secretKey.value);
    toast.success('Copied to clipboard!');
};

const copyEnvCode = () => {
    clipboard.copy(envCode.value);
    toast.success('Copied to clipboard!');
};

const revealSecretKey = async () => {
    const secret = await fetchSecretKey();

    if (secret === null) {
        secretKeyRevealed.value = false;
        secretKey.value = defaultSecretKey;
    } else {
        secretKey.value = secret;
        secretKeyRevealed.value = true;
    }
};

const fetchSecretKeySeen = async () => {
    try {
        const res = await axios.get(
            route('api-tokens.secret-token.seen.show', {
                apiToken: props.apiToken.id,
            })
        );

        secretKeySeen.value = res.data.seen;
    } catch (error) {
        console.error('Error fetching secret key seen status:', error);
    }
};

const fetchSecretKey = async () => {
    try {
        const res = await axios.get(
            route('api-tokens.secret-token.show', {
                apiToken: props.apiToken.id,
            })
        );
        return res.data.secret;
    } catch (error) {
        console.error('Error fetching secret key:', error);
    }
};

const regenerateSecretKey = () => {
    router.post(
        route('api-tokens.secret-token.store', {
            apiToken: props.apiToken.id,
        }),
        {},
        {
            preserveScroll: true,
            preserveState: true,
            onSuccess: () => {
                toast.success('Secret token regenerated');

                secretKey.value = defaultSecretKey;
                secretKeyRevealed.value = false;

                fetchSecretKeySeen();
            },
        }
    );
};
</script>
